import React, { useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import { quimeraPassword, quimeraUsername, RoutesPaths } from "../Router/constant";
import quimeraOrderService from "../services/quimeraOrder.service";
import { isValidEmailAddress } from "../utils/formValidation";
import "./QuimeraOrders.css";

const PAGE_SIZE = 8;
const EMPTY_ORDERS = [];
const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Inprogress", value: "inprogress" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const readText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value !== "object") return String(value);
  return String(
    value.name || value.fullName || value.companyName || value.customerName ||
      value.productName || value.title || value.label || value.value || value.id || "",
  );
};

const findValue = (object, keys) => {
  for (const key of keys) {
    const value = object?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const getOrderId = (order) =>
  readText(findValue(order, ["orderNumber", "bookingNumber", "bookingId", "trackingId", "referenceNumber", "orderId", "orderNo", "code", "_id", "id"])) || "—";

const getRouteId = (order) =>
  readText(findValue(order, ["_id", "id", "orderId", "bookingId", "bookingNumber", "orderNumber"])) || getOrderId(order);

const getOrderStatus = (order) => {
  const status = readText(findValue(order, ["status", "bookingStatus", "orderStatus", "shipmentStatus", "currentStatus"])) || "Pending";
  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  let tone = "pending";
  if (/cancel|reject|fail/.test(normalized)) tone = "cancelled";
  else if (/deliver|complete|success/.test(normalized)) tone = "delivered";
  else if (/transit|progress|ship|dispatch/.test(normalized)) tone = "in-progress";
  return { label: status.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()), tone };
};

const getProductName = (order) => {
  const products = order?.products || order?.items || order?.orderItems || order?.lines;
  const firstProduct = Array.isArray(products) ? products[0] : null;
  const product = order?.product || order?.commodity || firstProduct?.product || firstProduct?.item || firstProduct;
  return readText(product) || readText(findValue(order, ["productName", "commodityName", "itemName", "goods", "commodity", "productDescription"])) || "—";
};

const getQuantity = (order) => {
  const products = order?.products || order?.items || order?.orderItems || order?.lines;
  const firstProduct = Array.isArray(products) ? products[0] : null;
  const raw = findValue(order, ["quantity", "qty", "totalQuantity", "orderQuantity"]) ??
    findValue(firstProduct, ["quantity", "qty", "amount"]);
  if (raw && typeof raw === "object") {
    const amount = findValue(raw, ["amount", "value", "quantity"]);
    const unit = readText(raw.unit || raw.quantityUnit);
    return amount === undefined ? readText(raw) || "—" : `${amount}${unit ? ` ${unit}` : ""}`;
  }
  const unit = readText(findValue(order, ["quantityUnit", "unit", "unitOfMeasure"]) || firstProduct?.unit);
  return raw === undefined ? "—" : `${raw}${unit ? ` ${unit}` : ""}`;
};

const getOrderType = (order) =>
  readText(findValue(order, ["type", "shippingType", "freightType", "transportType", "shippingMethod", "shipmentType", "mode"])) || "—";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? readText(value)
    : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
};

const getOrderDate = (order) =>
  formatDate(findValue(order, ["bookingDate", "bookedAt", "createdAt", "createdOn", "orderDate", "date", "updatedAt"]));

const getBodyContainers = (body) => [
  body,
  body?.data,
  body?.result,
  body?.payload,
  body?.data?.data,
  body?.data?.result,
].filter((item) => item && typeof item === "object");

const parseOrderListResponse = (response) => {
  const body = response?.data ?? response;
  const containers = getBodyContainers(body);
  let orders = Array.isArray(body) ? body : null;
  if (!orders) {
    for (const container of containers) {
      const candidate = Array.isArray(container)
        ? container
        : container.orders || container.bookings || container.items || container.results || container.records;
      if (Array.isArray(candidate)) {
        orders = candidate;
        break;
      }
    }
  }
  const readNumber = (keys) => {
    for (const container of [...containers, ...containers.map((item) => item.pagination || item.meta || item.counts || item.summary).filter(Boolean)]) {
      const value = findValue(container, keys);
      const number = Number(value);
      if (value !== undefined && Number.isFinite(number)) return number;
    }
    return undefined;
  };
  const total = readNumber(["total", "totalCount", "totalOrders", "totalBookings", "recordsTotal", "count"]);
  const totalPages = readNumber(["totalPages", "pages", "pageCount"]);
  const hasMore = containers.some((container) =>
    container.hasNextPage === true || container.hasMore === true || container.pagination?.hasNextPage === true,
  );
  const inTransit = readNumber(["inTransit", "in_transit", "inTransitCount", "transitCount"]);
  const delivered = readNumber(["delivered", "deliveredCount", "completedCount"]);
  return {
    orders: orders || EMPTY_ORDERS,
    total: total ?? (orders || EMPTY_ORDERS).length,
    totalPages,
    hasMore,
    inTransit,
    delivered,
  };
};

const getApiError = (error, fallback = "Unable to load Quimera bookings.") => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.message;
  if (typeof message === "string" || typeof message === "number") return String(message);
  return message?.message || message?.error || fallback;
};

const padCount = (value) => String(Math.max(0, Number(value) || 0)).padStart(2, "0");

export default function QuimeraOrders() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(1);
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState(EMPTY_ORDERS);
  const [total, setTotal] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [inTransitCount, setInTransitCount] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [quimeraToken, setQuimeraToken] = useState("");
  const [authChecking, setAuthChecking] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const email = quimeraUsername;
  const password = quimeraPassword;
  const canConnect = isValidEmailAddress(email) && password.length > 0 && !connectLoading;
  const connectFormRef = useRef(null);

  useEffect(() => {
    let active = true;
    const savedToken = quimeraOrderService.getSavedQuimeraToken();
    if (!savedToken) {
      setQuimeraToken("");
      setAuthChecking(false);
      return () => { active = false; };
    }

    quimeraOrderService.verifyQuimeraToken(savedToken)
      .then((response) => {
        if (!active) return;
        if (quimeraOrderService.tokenIsRejected(response)) {
          quimeraOrderService.clearQuimeraToken();
          setQuimeraToken("");
          setAuthError("Your Quimera session expired. Please connect again.");
          return;
        }
        setQuimeraToken(savedToken);
      })
      .catch((requestError) => {
        if (!active) return;
        const requestStatus = requestError?.response?.status;
        if (requestStatus === 401 || requestStatus === 403) {
          quimeraOrderService.clearQuimeraToken();
          setAuthError("Your Quimera session expired. Please connect again.");
        } else {
          setAuthError(getApiError(requestError, "Unable to verify the Quimera session."));
        }
        setQuimeraToken("");
      })
      .finally(() => {
        if (active) setAuthChecking(false);
      });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (authChecking || !quimeraToken) {
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    setError("");
    quimeraOrderService.getQuimeraOrders({ status, offset, limit: PAGE_SIZE, token: quimeraToken })
      .then((response) => {
        if (!active) return;
        const parsed = parseOrderListResponse(response);
        setOrders(parsed.orders);
        setTotal(parsed.total);
        setTotalPagesFromApi(parsed.totalPages);
        setHasMore(parsed.hasMore);
        setInTransitCount(parsed.inTransit ?? parsed.orders.filter((order) => getOrderStatus(order).tone === "in-progress").length);
        setDeliveredCount(parsed.delivered ?? parsed.orders.filter((order) => getOrderStatus(order).tone === "delivered").length);
      })
      .catch((requestError) => {
        if (!active) return;
        const requestStatus = requestError?.response?.status;
        if (requestStatus === 401 || requestStatus === 403) {
          quimeraOrderService.clearQuimeraToken();
          setQuimeraToken("");
          setAuthError("Your Quimera session expired. Please connect again.");
          return;
        }
        setOrders(EMPTY_ORDERS);
        setTotal(0);
        setTotalPagesFromApi(undefined);
        setHasMore(false);
        setError(getApiError(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authChecking, offset, quimeraToken, reload, status]);

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) =>
      [getOrderId(order), readText(order.customer || order.client || order.buyer || order.user), getProductName(order)]
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [orders, search]);

  const totalPages = Math.max(totalPagesFromApi || Math.ceil(total / PAGE_SIZE) || 1, 1);
  const canGoNext = offset < totalPages || hasMore || orders.length >= PAGE_SIZE;
  const first = filteredOrders.length ? (offset - 1) * PAGE_SIZE + 1 : 0;
  const last = filteredOrders.length ? Math.min((offset - 1) * PAGE_SIZE + filteredOrders.length, total) : 0;

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setOffset(1);
  };

  const handleConnect = async (event) => {
    event.preventDefault();
    if (!canConnect) return;
    setConnectLoading(true);
    setAuthError("");
    try {
      const loginResponse = await quimeraOrderService.loginQuimera({ email: email.trim(), password });
      if (quimeraOrderService.tokenIsRejected(loginResponse)) {
        throw new Error(getApiError(loginResponse, "Quimera did not accept these credentials."));
      }
      const token = quimeraOrderService.getAccessToken(loginResponse);
      if (!token) {
        throw new Error(getApiError(loginResponse, "Quimera login did not return an access token."));
      }
      quimeraOrderService.saveQuimeraToken(token);
      const savedToken = quimeraOrderService.getSavedQuimeraToken();
      if (!savedToken) throw new Error("Quimera login token could not be read from localStorage.");
      const verificationResponse = await quimeraOrderService.verifyQuimeraToken();
      if (quimeraOrderService.tokenIsRejected(verificationResponse)) {
        throw new Error("Quimera could not verify the returned access token.");
      }
      setQuimeraToken(savedToken);
      setAuthError("");
      setStatus("");
      setOffset(1);
      setSearch("");
    } catch (connectError) {
      setAuthError(getApiError(connectError, "Unable to connect to Quimera. Check your email and password."));
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = () => {
    quimeraOrderService.clearQuimeraToken();
    setQuimeraToken("");
    setOrders(EMPTY_ORDERS);
    setTotal(0);
    setAuthError("");
  };


  if (authChecking) {
    return (
      <div className="dashboard-page quimera-page quimera-connect-page">
        <section className="quimera-not-connected-card quimera-auth-checking">
          <LoadingSpinner label="Checking Quimera connection..." />
        </section>
      </div>
    );
  }

  const submitConnectForm = () => connectFormRef.current?.requestSubmit();

  if (!quimeraToken) {
    return (
      <div className="dashboard-page quimera-page quimera-connect-page">
        <section className="quimera-connect-bar">
          <div className="quimera-connect-brand">
            <span className="quimera-connect-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M9.5 14.5 14.5 9.5M8 16l-1.2 1.2a3.4 3.4 0 0 1-4.8-4.8l4-4a3.4 3.4 0 0 1 4.8 0M16 8l1.2-1.2a3.4 3.4 0 0 1 4.8 4.8l-4 4a3.4 3.4 0 0 1-4.8 0" /></svg>
            </span>
            <span className="quimera-connect-title"><small>Quimera 360 orders</small><strong>Connect account</strong></span>
          </div>
          <form ref={connectFormRef} className="quimera-connect-form" autoComplete="off" onSubmit={handleConnect}>
            <label>
              <span>Email</span>
              <span className="quimera-connect-input"><i aria-hidden="true">@</i><input type="email" name="quimera-account-email" autoComplete="off" autoCapitalize="none" spellCheck={false} readOnly value={email} /></span>
            </label>
            <label>
              <span>Password</span>
              <span className="quimera-connect-input"><i aria-hidden="true">*</i><input type="password" name="quimera-account-password" autoComplete="new-password" readOnly value={password} /></span>
            </label>
            <button type="submit" className="quimera-connect-button" disabled={!canConnect}>
              {connectLoading ? "Connecting..." : "Connect"}
              {!connectLoading && <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 10h12m-4.5-4.5L15.5 10 11 14.5" /></svg>}
            </button>
          </form>
          {authError && <div className="quimera-connect-error" role="alert">{authError}</div>}
        </section>

        <section className="quimera-not-connected-card">
          <span className="quimera-connect-icon quimera-connect-icon-large" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M9.5 14.5 14.5 9.5M8 16l-1.2 1.2a3.4 3.4 0 0 1-4.8-4.8l4-4a3.4 3.4 0 0 1 4.8 0M16 8l1.2-1.2a3.4 3.4 0 0 1 4.8 4.8l-4 4a3.4 3.4 0 0 1-4.8 0" /></svg>
          </span>
          <span className="quimera-not-connected-badge">Not connected</span>
          <h1>Connect Quimera 360 to view orders</h1>
          <p>Your Quimera order feed is not connected yet. Connect your account first, then your latest bookings and shipment details will appear here.</p>
          <button type="button" className="quimera-connect-button quimera-connect-now" onClick={submitConnectForm} disabled={!canConnect}>
            {connectLoading ? "Connecting..." : "Connect now"}
            {!connectLoading && <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 10h12m-4.5-4.5L15.5 10 11 14.5" /></svg>}
          </button>
        </section>
      </div>
    );
  }
  return (
    <div className="dashboard-page quimera-page">
      <header className="quimera-page-header">
        <div className="quimera-heading-copy">
          <h1>Quimera 360 orders</h1>
          <p>Review all Quimera bookings and open each order to see full shipment details.</p>
          <div className="quimera-connected-note"><span />Connected to Quimera 360<button type="button" onClick={handleDisconnect}>Disconnect</button></div>
        </div>
        <div className="quimera-summary-cards" aria-label="Booking summary">
          <div className="quimera-summary-card"><span>Total</span><strong>{padCount(total)}</strong></div>
          <div className="quimera-summary-card"><span>In transit</span><strong>{padCount(inTransitCount)}</strong></div>
          <div className="quimera-summary-card"><span>Delivered</span><strong>{padCount(deliveredCount)}</strong></div>
        </div>
      </header>

      <section className="quimera-table-card" aria-label="Quimera bookings">
        <div className="quimera-toolbar">
          <label className="quimera-search">
            <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.8" /><path d="m13.2 13.2 4 4" /></svg>
            <input
              type="search"
              value={search}
              placeholder="Search booking..."
              aria-label="Search bookings"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="quimera-status-tabs" role="tablist" aria-label="Filter bookings by status">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value || "all"}
                type="button"
                role="tab"
                aria-selected={status === filter.value}
                className={status === filter.value ? "is-active" : ""}
                onClick={() => changeStatus(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <span className="quimera-result-count">
            {loading ? "Loading bookings..." : `${filteredOrders.length} of ${total} bookings`}
          </span>
        </div>

        <div className="quimera-table-scroll">
          <table className="quimera-orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Qty</th>
                <th>Type</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8"><LoadingSpinner label="Loading bookings..." /></td></tr>
              ) : error ? (
                <tr><td colSpan="8"><div className="quimera-empty-state" role="alert"><strong>Could not load bookings</strong><span>{error}</span><button type="button" onClick={() => setReload((current) => current + 1)}>Try again</button></div></td></tr>
              ) : filteredOrders.length ? (
                filteredOrders.map((order, index) => {
                  const orderStatus = getOrderStatus(order);
                  const routeId = getRouteId(order);
                  return (
                    <tr key={routeId || index}>
                      <td className="quimera-order-id">{getOrderId(order)}</td>
                      <td>{readText(order.customer || order.client || order.buyer || order.user || order.customerName || order.clientName) || "—"}</td>
                      <td>{getProductName(order)}</td>
                      <td>{getQuantity(order)}</td>
                      <td>{getOrderType(order)}</td>
                      <td className="quimera-date-cell">{getOrderDate(order)}</td>
                      <td><span className={`quimera-status-badge ${orderStatus.tone}`}><span />{orderStatus.label}</span></td>
                      <td>
                        <button
                          type="button"
                          className="quimera-view-button"
                          onClick={() => navigate(generatePath(RoutesPaths.QUIMERA_ORDER_DETAILS, { id: routeId }))}
                        >
                          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M2 10s2.8-4.7 8-4.7 8 4.7 8 4.7-2.8 4.7-8 4.7S2 10 2 10Z" /><circle cx="10" cy="10" r="2" /></svg>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="8"><div className="quimera-empty-state"><strong>No bookings found</strong><span>Try changing your search or status filter.</span></div></td></tr>
              )}
            </tbody>
          </table>
        </div>

        <footer className="quimera-table-footer">
          <span>Showing {first}–{last} of {total} bookings</span>
          <div className="quimera-pagination">
            <button type="button" disabled={offset <= 1 || loading} onClick={() => setOffset((current) => Math.max(1, current - 1))}>Previous</button>
            <button type="button" className="current-page" aria-current="page" disabled>{offset}</button>
            <button type="button" disabled={!canGoNext || loading} onClick={() => setOffset((current) => current + 1)}>Next</button>
          </div>
        </footer>
      </section>
    </div>
  );
}